import { useCallback, useState } from "react";
import _ from "lodash";

import { CloseIcon } from "icons";
import { colors } from "theme/palette";

import Button from "components/Button";
import FormControlLabel from "components/FormControlLabel";
import IconButton from "components/IconButton";
import Stack from "components/Stack";
import Switch from "components/Switch";
import Text from "components/Text";
import TextField from "components/TextField";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";

export interface ITableBuilder {
  table: Array<Array<string>>;
  onChange: ({ table }: { table: Array<Array<string>> }) => void;
  editableCells: Array<{ row: number; column: number }>;
  onEditableCellsChange: ({
    editableCells,
  }: {
    editableCells: Array<{ row: number; column: number }>;
  }) => void;
}

const TableBuilder = ({
  table,
  onChange,
  editableCells,
  onEditableCellsChange,
}: ITableBuilder) => {
  const [
    isEditableCellSelectionModeEnabled,
    setIsEditableCellSelectionModeEnabled,
  ] = useState(false);

  const { show: showConfirmationDialog } = useConfirmationDialog();

  const handleRowAdd = useCallback(() => {
    const newRow: Array<string> = [];

    _.range(table[0]?.length || 1).forEach(() => newRow.push(""));

    onChange({ table: [...table, newRow] });
  }, [table, onChange]);

  const handleColumnAdd = useCallback(() => {
    if (table.length === 0) {
      onChange({ table: [[""]] });
    } else {
      const handler = () => {
        const newTable = table.map((row) => [...row, ""]);

        onChange({ table: newTable });
      };

      if (table[0].length === 9) {
        showConfirmationDialog({
          onConfirm: handler,
          confirmButtonText: "Continue",
          confirmButtonColor: "primary",
          message:
            "Tables with too many columns might have styling issues. Are you sure you want to continue?",
        });
      } else {
        handler();
      }
    }
  }, [table, onChange, showConfirmationDialog]);

  const handleCellValueChange = useCallback(
    ({
      rowIndex,
      colIndex,
      newValue,
    }: {
      rowIndex: number;
      colIndex: number;
      newValue: string;
    }) => {
      const newTable = table.map((row, ri) =>
        row.map((col, ci) =>
          rowIndex === ri && colIndex === ci ? newValue : col
        )
      );

      onChange({ table: newTable });
    },
    [table, onChange]
  );

  const handleRowDrop = useCallback(
    ({ rowIndex }: { rowIndex: number }) => {
      const newTable = [...table];
      _.pullAt(newTable, [rowIndex]);

      onChange({ table: newTable });
    },
    [table, onChange]
  );

  const handleColDrop = useCallback(
    ({ colIndex }: { colIndex: number }) => {
      const newTable = table
        .map((row) => {
          const newRow = [...row];
          _.pullAt(newRow, [colIndex]);

          return newRow;
        })
        .filter((row) => row.length > 0);

      onChange({ table: newTable });
    },
    [table, onChange]
  );

  const isCellEditable = useCallback(
    ({ row, column }: { row: number; column: number }) =>
      editableCells.some((item) => item.row === row && item.column === column),
    [editableCells]
  );

  return (
    <>
      <Stack sx={{ overflowX: "auto" }}>
        <Stack
          display="grid"
          alignItems="center"
          gridTemplateColumns={`repeat(${table[0]?.length}, 1fr) 40px`}
        >
          {table.map((row, rowIndex) => (
            <>
              {row.map((item, colIndex) => (
                <Stack
                  sx={{
                    margin: 0.5,
                    minWidth: "150px",
                    backgroundColor: rowIndex === 0 ? colors.blue20 : undefined,
                  }}
                  key={`cell-${colIndex}-${isEditableCellSelectionModeEnabled}`}
                >
                  <TextField
                    value={item}
                    focused={isEditableCellSelectionModeEnabled}
                    onClick={
                      isEditableCellSelectionModeEnabled
                        ? () =>
                            onEditableCellsChange({
                              editableCells: _.xorWith(
                                editableCells,
                                [{ row: rowIndex, column: colIndex }],
                                _.isEqual
                              ),
                            })
                        : undefined
                    }
                    onChange={(event) =>
                      handleCellValueChange({
                        rowIndex,
                        colIndex,
                        newValue: event.target.value,
                      })
                    }
                    sx={{
                      fieldset: {
                        borderStyle: isCellEditable({
                          row: rowIndex,
                          column: colIndex,
                        })
                          ? "dashed"
                          : undefined,
                      },
                    }}
                  />
                </Stack>
              ))}
              <Tooltip title="Remove row">
                <IconButton onClick={() => handleRowDrop({ rowIndex })}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </>
          ))}
          {_.range(table[0]?.length || 0).map((colIndex) => (
            <Stack justifyContent="center" alignItems="center">
              <Tooltip title="Remove column">
                <IconButton onClick={() => handleColDrop({ colIndex })}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
          <span />
        </Stack>
      </Stack>

      <Stack flexDirection="row" justifyContent="space-between">
        <Stack alignItems="flex-start">
          <FormControlLabel
            label={
              <Text variant="text2" sx={{ fontWeight: 500 }}>
                Editable cell selection mode:
              </Text>
            }
            control={
              <Switch
                checked={isEditableCellSelectionModeEnabled}
                onClick={() =>
                  setIsEditableCellSelectionModeEnabled(
                    (prevValue) => !prevValue
                  )
                }
              />
            }
            labelPlacement="start"
            sx={{ marginLeft: "unset" }}
          />
          <Text variant="text3" color={colors.gray60}>
            Once the mode is enabled select the cells you'd like to be editable
            by users by clicking on them.
          </Text>
          <Text variant="text3" color={colors.gray60}>
            Cells with dashed borders can be edited by users.
          </Text>
          <Text variant="text3" color={colors.gray60}>
            Please note that if a user modifies a cell that references a
            checklist field that cell might stop working as expected.
          </Text>
        </Stack>

        <Stack
          flexShrink={0}
          direction="row"
          gap={1}
          justifyContent="flex-end"
          marginTop={3}
        >
          <Button onClick={handleRowAdd}>Add row</Button>
          <Button onClick={handleColumnAdd}>Add column</Button>
        </Stack>
      </Stack>
    </>
  );
};

export default TableBuilder;
